// Map a visitor country (ISO 3166-1 alpha-2, from @barrykilby/geo VisitorGeo)
// to the tax-authority copy used on the homepage's "Accounting made easy"
// feature tile. Complements `voiceLang.ts` as the geography-aware content
// layer for marketing surfaces.
//
// Why country-specific copy matters: the 6th tile's value prop is "your
// bookkeeping feeds the local tax regime without extra work". The more
// concretely we can name the authority and the scheme (HMRC / MTD, ATO /
// STP, etc.) the more trust it earns from that country's reader. Generic
// "built for local compliance" copy is fine as a fallback but doesn't sell.
//
// Add a country here as soon as we pick up meaningful visitor volume from
// it. Schemes referenced below are current as of 2026-04.
//
// Sources:
//   GB: HMRC Making Tax Digital for ITSA
//   IE: Revenue Online Service (ROS), Form 11
//   AU: ATO Single Touch Payroll (STP) + myGov
//   NZ: IRD myIR + GST filing
//   CA: CRA My Business Account, GST/HST, T2125
//   US: IRS Schedule C + state-level requirements
//   DE: Finanzamt, ELSTER, Umsatzsteuervoranmeldung
//   FR: DGFiP, URSSAF auto-entrepreneur regime
//   NL: Belastingdienst, BTW aangifte

interface TaxCopy {
  /** Short description used on the homepage feature tile. */
  description: string;
}

const GENERIC_DESCRIPTION =
  "Xero and QuickBooks exports built in, plus accountant-ready CSVs. Digital record-keeping for local tax compliance -- no spreadsheets, no hand-off headaches.";

const COUNTRY_COPY: Record<string, TaxCopy> = {
  GB: {
    description:
      "Xero and QuickBooks exports built in, plus accountant-ready CSVs. HMRC Making Tax Digital (MTD) record-keeping on every plan -- no spreadsheets, no hand-off headaches.",
  },
  IE: {
    description:
      "Xero and QuickBooks exports built in, plus accountant-ready CSVs. Income and VAT records ready for Revenue's ROS filings -- no spreadsheets, no hand-off headaches.",
  },
  AU: {
    description:
      "Xero and QuickBooks exports built in, plus accountant-ready BAS-ready CSVs. Digital income and GST records for ATO / myGov -- no spreadsheets, no hand-off headaches.",
  },
  NZ: {
    description:
      "Xero and QuickBooks exports built in, plus accountant-ready CSVs. GST-ready records that drop straight into IRD myIR -- no spreadsheets, no hand-off headaches.",
  },
  CA: {
    description:
      "QuickBooks and Xero exports built in, plus accountant-ready CSVs. GST/HST and T2125 records for CRA My Business Account -- no spreadsheets, no hand-off headaches.",
  },
  US: {
    description:
      "QuickBooks and Xero exports built in, plus accountant-ready CSVs. Schedule C-ready records for IRS filings and state sales-tax returns -- no spreadsheets, no hand-off headaches.",
  },
  DE: {
    description:
      "Xero, DATEV, and QuickBooks exports built in, plus accountant-ready CSVs. Umsatzsteuer records ready for ELSTER / Finanzamt -- no spreadsheets, no hand-off headaches.",
  },
  FR: {
    description:
      "QuickBooks and Xero exports built in, plus accountant-ready CSVs. Records ready for DGFiP and URSSAF auto-entrepreneur filings -- no spreadsheets, no hand-off headaches.",
  },
  NL: {
    description:
      "Xero and QuickBooks exports built in, plus accountant-ready CSVs. BTW aangifte records ready for Belastingdienst filings -- no spreadsheets, no hand-off headaches.",
  },
};

/**
 * Return the homepage tax-tile copy for a given visitor country. Falls back
 * to the generic "built for local compliance" wording when the country is
 * unmapped or null. `country` is expected to be an ISO 3166-1 alpha-2 code
 * as produced by `@barrykilby/geo`.
 */
export function taxCopyForCountry(country: string | null | undefined): string {
  if (!country) return GENERIC_DESCRIPTION;
  const upper = country.toUpperCase();
  return COUNTRY_COPY[upper]?.description ?? GENERIC_DESCRIPTION;
}
